/**
 * InputForm component for creating new datasets.
 * This component provides a form interface for users to input description
 * and upload required CSV files.
 */

import React, { useState, useRef } from "react";
import PopupWrapper from "../../../../common/pyramid/PopupWrapper";
import { Form, SimpleItem, GroupItem, Label } from "devextreme-react/form";
import { Button } from "devextreme-react/button";
import paperPlane from "assets/img/icons/upload.png";
import { osirisApi } from "../../../../../services/api/osiris";
import notify from "devextreme/ui/notify";

/**
 * Default popup configuration for the InputForm component
 * Defines dimensions, title and icon for the popup
 */
export const popupConfigs = {
  width: "80vh",
  height: "auto",
  title: "New Dataset",
  icon: "add",
};

/**
 * InputForm component allows users to create new datasets with description
 * and required file uploads.
 *
 * @param {Object} props - Component properties
 * @param {boolean} props.isOpen - Controls the visibility of the popup
 * @param {Function} props.onSuccess - Callback function when operation is successful
 * @param {string} props.accessToken - Authentication token for API requests
 */
const InputForm = ({ isOpen, onSuccess, accessToken }) => {
  /**
   * State management for the component
   */
  const [description, setDescription] = useState(""); // Dataset description
  const [files, setFiles] = useState({
    staticData: null,
    pd: null,
    lgd: null,
    cashflows: null,
  }); // Selected files for each category
  const [fileErrors, setFileErrors] = useState({
    staticData: null,
    pd: null,
    lgd: null,
    cashflows: null,
  }); // Validation errors for each file
  const [isLoading, setIsLoading] = useState(false); // Loading state for form submission
  const [isSubmitting, setIsSubmitting] = useState(false); // Submission state to prevent multiple submissions

  /**
   * Reference to the form component for potential direct manipulation
   */
  const formRef = useRef(null);

  /**
   * API service for dataset operations
   */
  const osiris = osirisApi();

  /**
   * Resets the form to its initial state
   * Clears all input fields and errors
   */
  const resetForm = () => {
    setDescription("");
    setFiles({
      staticData: null,
      pd: null,
      lgd: null,
      cashflows: null,
    });
    setFileErrors({
      staticData: null,
      pd: null,
      lgd: null,
      cashflows: null,
    });
    setIsSubmitting(false);
  };

  /**
   * Handles the popup close event
   * Resets the form and triggers the success callback
   */
  const handleClose = () => {
    if (!isSubmitting) {
      resetForm();
      onSuccess();
    }
  };

  /**
   * Handles file selection and validation for a specific field
   * Validates file type and updates state accordingly
   *
   * @param {string} field - The field identifier for the file input
   * @returns {Function} - Event handler function for the file input
   */
  const handleFileChange = (field) => (event) => {
    const file = event.target.files[0];
    validateAndSetFile(field, file);
  };

  /**
   * Validates and sets a file for a given field (shared by input change and drop)
   */
  const validateAndSetFile = (field, file) => {
    if (!file) return;
    const isCsvMime = file.type === "text/csv";
    const isCsvExt =
      typeof file.name === "string" && file.name.toLowerCase().endsWith(".csv");
    const isCsv = isCsvMime || isCsvExt;

    if (!isCsv) {
      setFiles((prev) => ({
        ...prev,
        [field]: file,
      }));
      setFileErrors((prev) => ({
        ...prev,
        [field]: "File type is not allowed",
      }));
      return;
    }

    setFiles((prev) => ({
      ...prev,
      [field]: file,
    }));
    setFileErrors((prev) => ({
      ...prev,
      [field]: null,
    }));
  };

  /**
   * Handles file drop for a specific field
   */
  const handleFileDrop = (field) => (event) => {
    event.preventDefault();
    if (isSubmitting) return;
    const fileList = event.dataTransfer?.files;
    const file = fileList && fileList[0];
    validateAndSetFile(field, file);
  };

  /**
   * Handles form submission
   * Validates inputs, sends data to the API, and handles success/error states
   */
  const handleSubmit = async () => {
    if (isSubmitting) return;

    // Check if description is filled
    if (!description) {
      notify("Please fill in all fields to continue", "error", 3000);
      return;
    }

    // Check if all required files are selected
    const requiredFiles = ["staticData", "pd", "lgd", "cashflows"];
    const missingFiles = requiredFiles.some((field) => !files[field]);

    if (missingFiles) {
      notify("Please fill in all fields to continue", "error", 3000);
      return;
    }

    // Check for file type errors
    if (Object.values(fileErrors).some((error) => error !== null)) {
      notify("Please correct file errors before continuing", "error", 3000);
      return;
    }

    try {
      setIsSubmitting(true);
      setIsLoading(true);
      await osiris.addDataset({
        description,
        staticData: files.staticData,
        pd: files.pd,
        lgd: files.lgd,
        cashflows: files.cashflows,
      });
      handleClose();
    } catch (error) {
      console.error("Error :", error.message);
      notify(error.message || "An error occurred", "error", 3000);
    } finally {
      setIsLoading(false);
      setIsSubmitting(false);
    }
  };

  /**
   * File input component for selecting and displaying a file
   *
   * @param {Object} props - Component properties
   * @param {string} props.label - Label text for the file input
   * @param {string} props.fieldKey - Key identifier for the file field
   * @returns {JSX.Element} - Rendered file input component
   */
  const FileInput = ({ label, fieldKey }) => {
    const inputRef = useRef(null);
    const hasError = fileErrors[fieldKey] !== null;

    return (
      <SimpleItem>
        <Label text={label} />
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: "4px",
            marginTop: "4px",
            border: "1px dashed #cbd5e1",
            borderRadius: "6px",
            padding: "8px",
          }}
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleFileDrop(fieldKey)}
        >
          <Button
            stylingMode="contained"
            text="Select file"
            icon={paperPlane}
            onClick={() => inputRef.current?.click()}
            disabled={isSubmitting}
          />
          <input
            ref={inputRef}
            type="file"
            accept=".csv"
            onChange={handleFileChange(fieldKey)}
            style={{ display: "none" }}
            disabled={isSubmitting}
          />
          <div style={{ fontSize: "13px", color: "#666", marginLeft: "4px" }}>
            {files[fieldKey]?.name || "No file selected"}
            {hasError && (
              <div
                style={{ color: "#DC2626", fontSize: "12px", marginTop: "2px" }}
              >
                {fileErrors[fieldKey]}
              </div>
            )}
          </div>
          <div
            style={{ fontSize: "12px", color: "#94a3b8", marginLeft: "4px" }}
          >
            Drop a .csv file here
          </div>
        </div>
      </SimpleItem>
    );
  };

  return (
    <PopupWrapper
      {...popupConfigs}
      isVisible={isOpen}
      onCancel={handleClose}
      onConfirm={handleSubmit}
      isConfirmDisabled={isSubmitting}
      isCancelDisabled={isSubmitting}
      showLoading={isLoading}
    >
      <Form ref={formRef}>
        {/* Basic Information Section */}
        <GroupItem caption="Informations" colCount={2}>
          <SimpleItem
            colSpan={2}
            dataField="description"
            editorType="dxTextBox"
            editorOptions={{
              value: description,
              onValueChanged: (e) => setDescription(e.value),
              placeholder: "Enter description",
              readOnly: isSubmitting,
            }}
            isRequired={true}
          >
            <Label text="Description" />
          </SimpleItem>
        </GroupItem>

        {/* File Upload Section */}
        <GroupItem
          caption="Upload input"
          colCount={2}
          stylingMode="outlined"
          cssClass="dx-field-item-content-transparent"
        >
          <FileInput label="Static data" fieldKey="staticData" />
          <FileInput label="PD" fieldKey="pd" />
          <FileInput label="LGD" fieldKey="lgd" />
          <FileInput label="EAD" fieldKey="cashflows" />
        </GroupItem>
      </Form>
    </PopupWrapper>
  );
};

export default InputForm;
